import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { Add, Delete, Replay } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { dataverseService } from '../services/dataverseService';
import { handleDataverseLoadError } from '../lib/dataverseErrorHandler';
import { orderService } from '../services/orderService';
import OrderFormDialog from '../components/OrderFormDialog';
import OrderStatusChip from '../components/OrderStatusChip';
import {
  ORDER_STATUS,
  ORDER_STATUS_OPTIONS,
  ORDER_TYPE_OPTIONS
} from '../data/orderTypes';

const nextStatusMap = {
  [ORDER_STATUS.NEW]: ORDER_STATUS.ASSIGNED,
  [ORDER_STATUS.ASSIGNED]: ORDER_STATUS.IN_PROGRESS,
  [ORDER_STATUS.IN_PROGRESS]: ORDER_STATUS.COMPLETED,
  [ORDER_STATUS.COMPLETED]: ORDER_STATUS.NEW,
  [ORDER_STATUS.REJECTED]: ORDER_STATUS.NEW
};

const OrdersPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const [residents, setResidents] = useState([]);
  const [locks, setLocks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [presetValues, setPresetValues] = useState({});
  const [typeFilter, setTypeFilter] = useState('Alle');
  const [statusFilter, setStatusFilter] = useState('Alle');
  const [searchTerm, setSearchTerm] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    try {
      const { residents: residentData, locks: lockData } = await dataverseService.getAssignmentsData();
      setResidents(residentData);
      setLocks(lockData);
      orderService.initializeDemoData(residentData, lockData);
      setOrders(orderService.getAll());
    } catch (loadError) {
      if (handleDataverseLoadError(loadError, navigate)) {
        return;
      }
      setError('Klarte ikke hente data for bestillinger.');
      console.error(loadError);
    }
  }, [navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (location.state?.orderPreset) {
      setPresetValues(location.state.orderPreset);
      setOpenDialog(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const residentNameMap = useMemo(
    () => residents.reduce((acc, resident) => ({ ...acc, [resident.residentId]: resident.name }), {}),
    [residents]
  );
  const lockNameMap = useMemo(
    () => locks.reduce((acc, lock) => ({ ...acc, [lock.lockId]: lock.name }), {}),
    [locks]
  );

  const filteredOrders = useMemo(() => orders.filter((order) => {
    const typeMatch = typeFilter === 'Alle' || order.type === typeFilter;
    const statusMatch = statusFilter === 'Alle' || order.status === statusFilter;
    const residentName = residentNameMap[order.residentId] || residentNameMap[order.toResidentId] || '';
    const lockName = lockNameMap[order.lockId] || order.lockName || '';
    const searchMatch = !searchTerm
      || order.description?.toLowerCase().includes(searchTerm.toLowerCase())
      || residentName.toLowerCase().includes(searchTerm.toLowerCase())
      || lockName.toLowerCase().includes(searchTerm.toLowerCase());
    return typeMatch && statusMatch && searchMatch;
  }), [orders, typeFilter, statusFilter, searchTerm, residentNameMap, lockNameMap]);

  const stats = useMemo(() => ({
    openOrders: orders.filter((order) => ![ORDER_STATUS.COMPLETED, ORDER_STATUS.REJECTED].includes(order.status)).length,
    setupOrders: orders.filter((order) => order.type === 'Oppsett').length,
    moveOrders: orders.filter((order) => order.type === 'Flytting').length,
    serviceOrders: orders.filter((order) => order.type === 'Service').length
  }), [orders]);

  const handleCreateOrder = (payload) => {
    const account = accounts[0];
    orderService.create({
      ...payload,
      createdBy: account?.name || account?.username || 'System'
    });
    setOrders(orderService.getAll());
    setOpenDialog(false);
    setPresetValues({});
    setSuccessMessage('Bestilling opprettet.');
  };

  const handleCycleStatus = (order) => {
    orderService.updateStatus(order.id, nextStatusMap[order.status] || ORDER_STATUS.NEW);
    setOrders(orderService.getAll());
  };

  const handleDelete = (orderId) => {
    orderService.remove(orderId);
    setOrders(orderService.getAll());
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} gutterBottom>
        Bestillinger
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Opprett oppsett, flytting og service-oppdrag for låser.
      </Typography>

      {successMessage && (
        <Alert sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card><CardContent><Typography variant="h5">{stats.openOrders}</Typography><Typography variant="body2">Åpne</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card><CardContent><Typography variant="h5">{stats.setupOrders}</Typography><Typography variant="body2">Oppsett</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card><CardContent><Typography variant="h5">{stats.moveOrders}</Typography><Typography variant="body2">Flytting</Typography></CardContent></Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card><CardContent><Typography variant="h5">{stats.serviceOrders}</Typography><Typography variant="body2">Service</Typography></CardContent></Card>
        </Grid>
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Type"
                select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="Alle">Alle</MenuItem>
                {ORDER_TYPE_OPTIONS.map((type) => <MenuItem key={type} value={type}>{type}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Status"
                select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="Alle">Alle</MenuItem>
                {ORDER_STATUS_OPTIONS.map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Søk"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Beskrivelse, innbygger eller lås"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<Add />}
                onClick={() => setOpenDialog(true)}
              >
                Ny bestilling
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        {filteredOrders.map((order) => (
          <Grid item xs={12} key={order.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      {order.type} - {order.description}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Opprettet av {order.createdBy} - {new Date(order.createdAt).toLocaleString('nb-NO')}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <OrderStatusChip status={order.status} />
                      <Typography variant="body2">Prioritet: {order.priority}</Typography>
                    </Stack>
                    {order.type === 'Oppsett' && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Innbygger: {residentNameMap[order.residentId] || order.residentId || 'Ikke valgt'} - Lås: {order.lockName || 'Ikke angitt'}
                      </Typography>
                    )}
                    {order.type === 'Flytting' && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Lås: {lockNameMap[order.lockId] || order.lockId || 'Ikke valgt'} - Fra: {residentNameMap[order.fromResidentId] || order.fromResidentId || 'Ukjent'} - Til: {residentNameMap[order.toResidentId] || order.toResidentId || 'Ikke valgt'}
                      </Typography>
                    )}
                    {order.type === 'Service' && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Lås: {lockNameMap[order.lockId] || order.lockId || 'Ikke valgt'} - Kategori: {order.issueCategory || 'Ikke angitt'}
                      </Typography>
                    )}
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Replay />}
                      onClick={() => handleCycleStatus(order)}
                    >
                      Neste status
                    </Button>
                    <IconButton color="error" onClick={() => handleDelete(order.id)}>
                      <Delete />
                    </IconButton>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {filteredOrders.length === 0 && (
        <Card><CardContent><Typography color="text.secondary">Ingen bestillinger matcher filteret.</Typography></CardContent></Card>
      )}

      <OrderFormDialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setPresetValues({});
        }}
        onSubmit={handleCreateOrder}
        residents={residents}
        locks={locks}
        initialValues={presetValues}
      />
    </Box>
  );
};

export default OrdersPage;
